import { isAfter, isWithinInterval } from '@if/core/date'
import { ECreditApplicationStatus, EDecision } from '@if/core/types'
import { formatUtcIsoDateTime } from '@if/core/utils'
import { CircularProgress, Typography } from '@mui/material'
import { EIconType } from '@src/containers/App/components/iconSection'
import { anyDocsAwaitingApproval } from '@src/data/creditapp-selectors'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import StepStatusSection from '../../../App/components/StepStatusSection'
import { canPollCreditApp, isServerAnalysingCredit, mustAskApplicantSIN, mustAskCoapplicantSIN } from '../../credit-hooks'
import { ECreditApplicationStatusMessage, GetDefaultIconAndMessageFromDecision } from '../../viewCreditApp-selectors'
import ApplicationError from '../applicationError'
import PrequalApproved from './PrequalApproved'
import PrequalCreditReportNotFound from './PrequalCreditReportNotFound'
import PrequalDeclined from './PrequalDeclined'
import PrequalDocumentRequired from './PrequalDocumentRequired'
import PrequalNewToCredit from './PrequalNewToCredit'
import PrequalNotStarted from './PrequalNotStarted'

interface Props {
  creditApp: CreditApplication
  merchant: Merchant
}

const PrequalTile = ({ creditApp, merchant }: Props) => {
  const { t } = useTranslation()
  const isAnalysing = isServerAnalysingCredit(creditApp) && canPollCreditApp(creditApp)

   const activePaymentPlans = useMemo(() => {
  if (!merchant?.paymentPlans) return []
  const currentDate = new Date()
  const DISTANT_FUTURE = new Date()
  DISTANT_FUTURE.setFullYear(DISTANT_FUTURE.getFullYear() + 100)
  const currentUtcDateFormatted = new Date(formatUtcIsoDateTime(new Date()))

    return merchant.paymentPlans.filter((plan) => {
      const planActiveFrom = new Date(plan.activeFrom)
     const planExpiresOn = new Date(plan?.expiresOn ?? DISTANT_FUTURE)
      return (
        isWithinInterval(currentDate, { start: planActiveFrom, end: planExpiresOn }) &&
        isAfter(planExpiresOn, currentUtcDateFormatted)
      )
    })
  }, [merchant?.paymentPlans])

  const hasActivePaymentPlans = activePaymentPlans.length > 0

  const docsAwaitingApproval = anyDocsAwaitingApproval(creditApp.requiredDocuments)
  const needsSIN = mustAskApplicantSIN(creditApp) || mustAskCoapplicantSIN(creditApp)
  const isRefusedWithReportNotFound =
    creditApp.prequalificationDecision?.decision === EDecision.Refused &&
    (!creditApp.applicant.hasCreditReport || (creditApp.coApplicant && creditApp.coApplicant.hasCreditReport === false))

  const icon =
    creditApp.prequalificationDecision?.decision === EDecision.Approved && needsSIN
      ? {
          iconMessage: ECreditApplicationStatusMessage.SinRequired,
          iconType: EIconType.warning,
        }
      : isRefusedWithReportNotFound && creditApp.status !== ECreditApplicationStatus.Cancelled
      ? {
          iconMessage: ECreditApplicationStatusMessage.CreditReportNotFound,
          iconType: EIconType.error,
        }
      : GetDefaultIconAndMessageFromDecision(
          creditApp.prequalificationDecision?.decision,
          true,
          creditApp,
          isAnalysing,
          docsAwaitingApproval,
        )

  return (
    <StepStatusSection
      title={t('viewCreditApplication.prequalificationSectionTitle')}
      iconMessage={icon.iconMessage ? t(`creditApplicationStatus.${icon.iconMessage}`) : null}
      iconType={icon.iconType}
    >
      {creditApp.status === ECreditApplicationStatus.Cancelled &&
        creditApp.prequalificationDecision?.decision !== EDecision.Approved && (
          <Typography variant="body1">{t('viewCreditApplication.creditAppCancelled')}</Typography>
        )}
      {creditApp.status === ECreditApplicationStatus.Draft && <PrequalNotStarted />}
      {creditApp.status === ECreditApplicationStatus.Active &&
        isAnalysing &&
        creditApp.prequalificationDecision === null && (
          <CircularProgress style={{ margin: 'auto', display: 'block' }} />
        )}

      {creditApp.prequalificationDecision?.decision === EDecision.Approved && needsSIN && (
        <PrequalNewToCredit creditApp={creditApp} />
      )}
      {creditApp.prequalificationDecision?.decision === EDecision.Approved && !needsSIN && (
        <PrequalApproved
          creditApp={creditApp}
          merchant={merchant}
          hasActivePaymentPlans={hasActivePaymentPlans}
        />
      )}

      {icon.iconMessage === ECreditApplicationStatusMessage.Error && <ApplicationError />}
      {(icon.iconMessage === ECreditApplicationStatusMessage.Declined || !creditApp.consentSoftHit) && (
        <PrequalDeclined creditApp={creditApp} />
      )}
      {icon.iconMessage === ECreditApplicationStatusMessage.DocumentsRequired && (
        <PrequalDocumentRequired creditApp={creditApp} />
      )}
      {icon.iconMessage === ECreditApplicationStatusMessage.AwaitingApproval && (
        <Typography>{t('viewCreditApplication.clientManualRevision')}</Typography>
      )}
      {icon.iconMessage === ECreditApplicationStatusMessage.CreditReportNotFound && (
        <PrequalCreditReportNotFound creditApp={creditApp} />
      )}
    </StepStatusSection>
  )
}

export default PrequalTile
